import type { ChildProcess } from 'node:child_process';
import { isCliImmediateShutdownRequested } from '../runtime/cli-process-interrupt.js';

const activeGitChildProcesses = new Set<ChildProcess>();

/** Register a git subprocess so it can be killed on CLI interrupt. */
export function trackGitChildProcess(child: ChildProcess): void {
  activeGitChildProcesses.add(child);
  const untrack = () => {
    activeGitChildProcesses.delete(child);
  };
  child.once('close', untrack);
  child.once('error', untrack);
}

/** Kill in-flight git subprocesses when the CLI is shutting down. */
export function abortActiveGitChildProcesses(): void {
  for (const child of activeGitChildProcesses) {
    try {
      child.kill('SIGTERM');
    } catch {
      // ignore
    }
  }
}

export class GitOperationAbortedError extends Error {
  constructor(message = 'Git operation aborted') {
    super(message);
    this.name = 'GitOperationAbortedError';
  }
}

/** Throws when the CLI received SIGINT/SIGTERM or bridge close requested shutdown. */
export function throwIfGitShutdownRequested(): void {
  if (isCliImmediateShutdownRequested()) {
    abortActiveGitChildProcesses();
    throw new GitOperationAbortedError();
  }
}

/** Run an async git operation with pre/post shutdown checks. */
export async function runGitTask<T>(fn: () => Promise<T>): Promise<T> {
  throwIfGitShutdownRequested();
  try {
    const result = await fn();
    throwIfGitShutdownRequested();
    return result;
  } catch (e) {
    if (isCliImmediateShutdownRequested()) {
      throw new GitOperationAbortedError();
    }
    throw e;
  }
}

/** Yield to the event loop between chunks of git work. */
export async function yieldToEventLoop(): Promise<void> {
  throwIfGitShutdownRequested();
  await new Promise<void>((resolve) => {
    setImmediate(resolve);
  });
  throwIfGitShutdownRequested();
}

/** Run `fn` for each item, yielding before every item so the bridge stays responsive. */
export async function forEachWithGitYield<T>(
  items: readonly T[],
  fn: (item: T, index: number) => Promise<void>,
): Promise<void> {
  for (let i = 0; i < items.length; i++) {
    await yieldToEventLoop();
    await fn(items[i]!, i);
  }
}
